<script setup lang="ts">
    import Icon from '@/components/Icon.vue';
    import type { StatItem } from '@/types';
    import { computed, ref } from 'vue';
    import type { Table } from '@tanstack/vue-table';
    import type { DashboardTableData } from '@/types';
    import { useDashboardStore } from '@/stores/dashboard';

    const props = defineProps<{
        table?: Table<DashboardTableData>;
        item: StatItem;
    }>();

    const dashboardStore = useDashboardStore();

    const isActive = computed(() => dashboardStore.activeFilterKey === props.item.key);

    const hasFilterValues = computed(() => {
        return props.item.filter && !!props.item.filter.value.length;
    });

    const handleClick = () => {
        if (dashboardStore.activeFilterKey === props.item.key || props.item.isReset) {
            props.table?.resetColumnFilters();
            dashboardStore.resetActiveFilter();
        } else {
            props.table?.setColumnFilters([props.item.filter]);
            dashboardStore.setActiveFilter(props.item);
        }
    };
</script>

<template>
    <div
        class="flex max-w-[200px] shrink-0 items-center gap-2 rounded-md border px-1 2xl:shrink"
        :class="[
            isActive ? 'bg-destructive/5 border-destructive/10' : 'border-transparent',
            (item.isReset || hasFilterValues) ? 'cursor-pointer' : 'pointer-events-none',
        ]"
        @click="(item.isReset || hasFilterValues) && handleClick()"
    >
        <component
            v-if="item.customIcon"
            :is="item.customIcon"
            className="text-destructive size-4 shrink-0 2xl:size-6"
        />
        <Icon
            v-else-if="item.icon"
            :name="item.icon"
            class="text-destructive size-4 shrink-0 2xl:size-6"
        />
        <span class="text-lg font-medium 2xl:text-2xl">{{ item.value }}</span>
        <span class="text-muted-foreground text-[10px] leading-none">
            {{ item.description }}
        </span>
    </div>
</template>
